import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal, Loader2 } from "lucide-react";
import { Layout } from "@/components/Layout";
import { JobCard } from "@/components/JobCard";
import { api } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";

const SELECTS = [
  { key: "work_mode", label: "Work mode", opts: ["", "remote", "hybrid", "on-site"] },
  { key: "employment_type", label: "Type", opts: ["", "full-time", "part-time", "contract", "freelance"] },
];

export default function Jobs() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const [filters, setFilters] = useState({
    q: "", city: "", work_mode: params.get("work_mode") || "",
    employment_type: params.get("employment_type") || "",
  });
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState(true);

  const run = useCallback(async () => {
    setBusy(true);
    try {
      const clean = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const { data } = await api.get("/jobs/search", { params: clean });
      setResults(data.results); setTotal(data.total);
    } catch { /* ignore */ } finally { setBusy(false); }
  }, [filters]);

  useEffect(() => { run(); /* eslint-disable-next-line */ }, []);

  return (
    <Layout>
      <div className="bg-navy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <h1 className="font-display text-3xl font-extrabold text-white">{t("nav_find_jobs")}</h1>
          <form onSubmit={(e) => { e.preventDefault(); run(); }} className="mt-6 flex flex-col sm:flex-row gap-3">
            <div className="flex-1 flex items-center gap-2 rounded-xl bg-white px-4 py-3">
              <Search className="h-5 w-5 text-slate-400" />
              <input data-testid="jobs-search-q" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                placeholder={t("search_placeholder")} className="w-full text-sm focus:outline-none" />
            </div>
            <div className="flex items-center gap-2 rounded-xl bg-white px-4 py-3 sm:w-56">
              <input data-testid="jobs-search-city" value={filters.city} onChange={(e) => setFilters({ ...filters, city: e.target.value })}
                placeholder={t("city_placeholder")} className="w-full text-sm focus:outline-none" />
            </div>
            <button type="submit" data-testid="jobs-search-btn" className="gjt-btn-orange rounded-xl px-8 py-3 text-sm font-bold">{t("search")}</button>
          </form>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-wrap items-center gap-3">
          <SlidersHorizontal className="h-4 w-4 text-slate-400" />
          {SELECTS.map((s) => (
            <select key={s.key} data-testid={`jobs-filter-${s.key}`} value={filters[s.key]}
              onChange={(e) => { const nf = { ...filters, [s.key]: e.target.value }; setFilters(nf); }}
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-navy capitalize focus:outline-none">
              {s.opts.map((o) => <option key={o} value={o}>{o || s.label}</option>)}
            </select>
          ))}
          <button onClick={run} className="rounded-xl bg-navy px-4 py-2 text-sm font-semibold text-white">Apply</button>
          <span className="ml-auto text-sm text-slate-500">{total} jobs</span>
        </div>

        {busy ? (
          <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-brand-blue" /></div>
        ) : (
          <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {results.map((j, i) => <JobCard key={j.id} job={j} index={i} />)}
          </div>
        )}
        {!busy && results.length === 0 && <p className="py-20 text-center text-slate-400">No jobs found. Try broadening your search.</p>}
      </div>
    </Layout>
  );
}
